import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Users, Loader2, LogOut, ArrowLeft, Briefcase, ExternalLink, ShieldOff, ShieldCheck, Ban, RotateCcw, Lock, Trash2, AlertTriangle,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  ACTIVE: 'bg-green-100 text-green-800 border-green-300',
  DEACTIVATED: 'bg-slate-200 text-slate-700 border-slate-300',
  SUSPENDED: 'bg-red-100 text-red-800 border-red-300',
  DELETED: 'bg-red-200 text-red-900 border-red-400',
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
};

const ACTIONS = [
  { key: 'deactivate', label: 'Deactivate', Icon: ShieldOff, variant: 'outline',
    desc: 'Marks the account inactive and blocks owner-workspace login. No data is deleted.' },
  { key: 'suspend', label: 'Suspend', Icon: Ban, variant: 'outline',
    desc: 'Suspends access and blocks owner-workspace login. No data is deleted.' },
  { key: 'activate', label: 'Activate', Icon: ShieldCheck, variant: 'default',
    desc: 'Re-enables the account and restores owner-workspace login.' },
  { key: 'restore', label: 'Restore', Icon: RotateCcw, variant: 'default',
    desc: 'Restores access. All existing data is preserved.' },
];

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return '—'; }
}
function fmtDateTime(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy h:mm a'); } catch { return '—'; }
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-3 rounded-lg border bg-muted/30">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold" data-testid={testid}>{value}</p>
    </div>
  );
}

export default function AdminClientDetailPage() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // pending action key
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState('recoverable');
  const [deleteWorkspace, setDeleteWorkspace] = useState('client');
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/clients/${clientId}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load client');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/admin/clients/${clientId}/status`, { action: pending }, { headers });
      toast.success('Account status updated');
      setPending(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update status');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); };
  const pendingAction = ACTIONS.find((a) => a.key === pending);

  const runRestore = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/clients/${clientId}/restore`, {}, { headers });
      toast.success('Client account restored');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore');
    } finally { setSaving(false); }
  };

  const openDelete = () => {
    setDeleteMode('recoverable');
    setDeleteWorkspace(data?.hasProviderWorkspace ? 'client' : 'client');
    setConfirmText('');
    setDeleteOpen(true);
  };

  const runDelete = async () => {
    if (deleteMode === 'permanent' && confirmText !== 'DELETE') return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/clients/${clientId}/delete`, {
        mode: deleteMode,
        workspace: data?.hasProviderWorkspace ? deleteWorkspace : 'client',
        confirm: deleteMode === 'permanent' ? confirmText : undefined,
      }, { headers });
      toast.success(deleteMode === 'permanent' ? 'Account permanently deleted' : 'Account deleted (recoverable for 90 days)');
      setDeleteOpen(false);
      if (deleteMode === 'permanent' && (deleteWorkspace === 'client' || deleteWorkspace === 'both')) {
        navigate('/admin/clients');
      } else {
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete account');
    } finally { setSaving(false); }
  };

  const isDeleted = data?.accountStatus === 'DELETED';

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <div className="flex items-center gap-4">
            <Logo variant="compact" size="sm" />
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')} data-testid="back-to-clients">
              <ArrowLeft className="h-4 w-4 mr-1" /> Client Accounts
            </Button>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" /><span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : !data ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">Client not found.</CardContent></Card>
        ) : (
          <>
            {/* Recoverable-delete banner */}
            {isDeleted && (
              <Card className="border-red-300 bg-red-50" data-testid="deleted-banner">
                <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    <span>
                      This account was deleted{data.deletedAt ? ` on ${fmtDate(data.deletedAt)}` : ''}. Recoverable
                      {data.deleteExpiresAt ? ` until ${fmtDate(data.deleteExpiresAt)}` : ' for 90 days'}.
                    </span>
                  </div>
                  <Button onClick={runRestore} disabled={saving} data-testid="restore-client-btn">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Restore account
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Overview */}
            <Card data-testid="client-overview">
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" /> {data.name || '(no name)'}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{data.email}{data.phone ? ` · ${data.phone}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${STATUS_BADGE[data.accountStatus] || ''} border`} data-testid="detail-status">{data.accountStatus}</Badge>
                    {data.hasProviderWorkspace && (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-300 border" data-testid="detail-dual">Client + Provider</Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 pt-2">
                  <Lock className="h-3 w-3" /> Read-only overview — personal details, animals, notes and bookings cannot be edited here.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-xs text-muted-foreground">Signup date</p><p>{fmtDate(data.signupDate)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Last login</p><p>{data.lastLoginAt ? fmtDateTime(data.lastLoginAt) : 'Not tracked'}</p></div>
                  <div><p className="text-xs text-muted-foreground">First booking</p><p>{fmtDate(data.firstBookingDate)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Most recent booking</p><p>{fmtDate(data.mostRecentBookingDate)}</p></div>
                </div>
                {data.hasProviderWorkspace && (
                  <div className="p-3 rounded-lg border bg-purple-50/50 text-sm flex items-center justify-between gap-2">
                    <span>This login also has a provider workspace: <strong>{data.linkedProviderName || '(provider)'}</strong></span>
                    {data.linkedProviderId && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin?provider=${data.linkedProviderId}`)}
                        data-testid="open-linked-provider">
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Provider record
                      </Button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Stat label="Providers" value={data.providerCount} testid="stat-providers" />
                  <Stat label="Total bookings" value={data.totalBookings} testid="stat-total" />
                  <Stat label="Upcoming" value={data.upcomingBookings} testid="stat-upcoming" />
                  <Stat label="Completed" value={data.completedBookings} testid="stat-completed" />
                  <Stat label="Cancelled" value={data.cancelledBookings} testid="stat-cancelled" />
                </div>
              </CardContent>
            </Card>

            {/* Status actions */}
            <Card>
              <CardHeader><CardTitle className="text-base">Account access</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2" data-testid="status-actions">
                {ACTIONS.map((a) => (
                  <Button key={a.key} variant={a.variant}
                    onClick={() => setPending(a.key)}
                    data-testid={`action-${a.key}`}>
                    <a.Icon className="h-4 w-4 mr-1.5" /> {a.label}
                  </Button>
                ))}
                <Button variant="ghost" className="text-destructive ml-auto"
                  onClick={openDelete} data-testid="delete-client-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete Client Account
                </Button>
              </CardContent>
            </Card>

            {/* Provider relationships */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-4 w-4" /> Provider relationships ({data.providerRelationships?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="provider-relationships">
                {(!data.providerRelationships || data.providerRelationships.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No provider relationships yet.</p>
                ) : data.providerRelationships.map((r) => (
                  <div key={r.providerId} className="rounded-lg border p-3" data-testid={`relationship-${r.providerId}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{r.businessName}</p>
                          <Badge className={`${STATUS_BADGE[r.providerAccountStatus] || ''} border text-xs`}>{r.providerAccountStatus}</Badge>
                          {(r.providerCategories?.length ? r.providerCategories : [r.providerCategory]).filter(Boolean).map((cat) => (
                            <Badge key={cat} variant="outline" className="text-[10px]">{String(cat).toLowerCase()}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {r.relationshipSource ? `Source: ${r.relationshipSource}` : 'Relationship via bookings'} ·
                          {' '}First {fmtDate(r.firstBookingDate)} · Recent {fmtDate(r.mostRecentBookingDate)}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin?provider=${r.providerId}`)}
                        data-testid={`open-provider-${r.providerId}`}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Provider
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 text-center text-sm">
                      <div><p className="text-xs text-muted-foreground">Total</p><p className="font-semibold" data-testid={`rel-total-${r.providerId}`}>{r.totalBookings}</p></div>
                      <div><p className="text-xs text-muted-foreground">Upcoming</p><p className="font-semibold">{r.upcomingBookings}</p></div>
                      <div><p className="text-xs text-muted-foreground">Completed</p><p className="font-semibold">{r.completedBookings}</p></div>
                      <div><p className="text-xs text-muted-foreground">Cancelled</p><p className="font-semibold">{r.cancelledBookings}</p></div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Confirm dialog */}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="status-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{pendingAction?.label} this client account?</DialogTitle>
            <DialogDescription>{pendingAction?.desc}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This affects only the client (owner) workspace. The linked provider workspace, bookings, animals,
            notes and relationships are not changed or deleted.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={saving}>Cancel</Button>
            <Button onClick={runAction} disabled={saving} data-testid="confirm-status-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Confirm {pendingAction?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog (recoverable / permanent + dual-role workspace choice) */}
      <Dialog open={deleteOpen} onOpenChange={(o) => !o && setDeleteOpen(false)}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Delete client account
            </DialogTitle>
            <DialogDescription>
              Provider history is always kept — bookings, the client's name in provider records, animals,
              notes and invoices are never removed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Dual-role workspace choice */}
            {data?.hasProviderWorkspace && (
              <div data-testid="delete-workspace-choice">
                <p className="text-sm font-medium mb-1">This login has two workspaces — what should be deleted?</p>
                {[
                  ['client', 'Client workspace only'],
                  ['provider', 'Provider workspace only'],
                  ['both', 'Both workspaces'],
                ].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 py-1 cursor-pointer text-sm" data-testid={`del-ws-${v}`}>
                    <input type="radio" name="del-ws" value={v} checked={deleteWorkspace === v}
                      onChange={() => setDeleteWorkspace(v)} className="h-4 w-4 accent-primary" />
                    {l}
                  </label>
                ))}
              </div>
            )}

            {/* Mode choice */}
            <div>
              <p className="text-sm font-medium mb-1">Deletion type</p>
              <label className="flex items-start gap-2 py-1 cursor-pointer text-sm" data-testid="del-mode-recoverable">
                <input type="radio" name="del-mode" value="recoverable" checked={deleteMode === 'recoverable'}
                  onChange={() => setDeleteMode('recoverable')} className="h-4 w-4 mt-0.5 accent-primary" />
                <span><strong>Recoverable</strong> — blocks login, hides from the list, recoverable for 90 days.</span>
              </label>
              <label className="flex items-start gap-2 py-1 cursor-pointer text-sm" data-testid="del-mode-permanent">
                <input type="radio" name="del-mode" value="permanent" checked={deleteMode === 'permanent'}
                  onChange={() => setDeleteMode('permanent')} className="h-4 w-4 mt-0.5 accent-primary" />
                <span><strong>Permanent</strong> — removes the client-owned account. No recovery.</span>
              </label>
            </div>

            {deleteMode === 'permanent' && data?.hasProviderWorkspace && deleteWorkspace !== 'client' && (
              <p className="text-xs text-muted-foreground" data-testid="provider-retain-note">
                Note: the provider workspace loses access permanently, but its records and booking history are retained.
              </p>
            )}

            {deleteMode === 'permanent' && (
              <div className="p-3 rounded-md border border-red-200 bg-red-50">
                <p className="text-sm text-red-800 mb-2">Type <strong>DELETE</strong> to confirm permanent deletion:</p>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE" data-testid="delete-confirm-input" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="destructive" onClick={runDelete}
              disabled={saving || (deleteMode === 'permanent' && confirmText !== 'DELETE')}
              data-testid="confirm-delete-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {deleteMode === 'permanent' ? 'Permanently delete' : 'Delete (recoverable)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
